import type { CaseSeries } from "@frontend/pages/case-workspace/radiology/types"
import type { ScoreAnnotationState } from "@shared/lib/history-types"
import { calculatePIQUALScore, type PIQUALScore } from "@shared/lib/piqual-scoring"
import { useCallback, useMemo, useState } from "react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BooleanFieldKey = (typeof PIQUAL_QUESTION_FIELDS)[number]

interface Question {
  id: BooleanFieldKey
  text: string
}

interface Section {
  title: string
  scoreKey: "t2_wi_score" | "dwi_score" | "dce_criteria_met"
  maxScore: number
  questions: Question[]
}

// ---------------------------------------------------------------------------
// Section definitions (matching old vxAnnotate)
// ---------------------------------------------------------------------------

const PIQUAL_QUESTION_FIELDS = [
  "axial_t2_snr",
  "axial_t2_delineate",
  "axial_t2_artifacts",
  "sag_coronal_t2_snr_and_artifacts",
  "dwi_contrast",
  "dwi_adc",
  "dwi_artifacts",
  "dwi_matching_adc_t2",
  "dce_artifacts",
  "dce_structures"
] as const

const SECTIONS: Section[] = [
  {
    title: "Axial T2",
    scoreKey: "t2_wi_score",
    maxScore: 4,
    questions: [
      { id: "axial_t2_snr", text: "Adequate signal-to-noise (SNR) in all parts of the images?" },
      { id: "axial_t2_delineate", text: "Ability to clearly delineate relevant structures in the prostate?" },
      { id: "axial_t2_artifacts", text: "Absence of significant artefacts in the prostatic region?" }
    ]
  },
  {
    title: "Sagittal or Coronal T2",
    scoreKey: "t2_wi_score",
    maxScore: 4,
    questions: [
      {
        id: "sag_coronal_t2_snr_and_artifacts",
        text: "Adequate SNR and image resolution AND absence of significant artefacts?"
      }
    ]
  },
  {
    title: "DWI",
    scoreKey: "dwi_score",
    maxScore: 4,
    questions: [
      { id: "dwi_contrast", text: "Adequate contrast and SNR on high b-value images?" },
      { id: "dwi_adc", text: "Adequate range of contrast to differentiate TZ/BPH from PZ on the ADC maps?" },
      { id: "dwi_artifacts", text: "Absence of significant artefacts in the prostatic region?" },
      { id: "dwi_matching_adc_t2", text: "Anatomical matching of the ADC map / high b-value sequence to the axial T2?" }
    ]
  },
  {
    title: "DCE",
    scoreKey: "dce_criteria_met",
    maxScore: 2,
    questions: [
      {
        id: "dce_artifacts",
        text: "Absence of significant artefacts in the prostatic region and appropriate bolus enhancement?"
      },
      {
        id: "dce_structures",
        text: "Ability to identify anatomical structures (e.g. capsular vessels, pudendal artery)."
      }
    ]
  }
]

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PiqualModulePanelProps {
  moduleId: string
  seriesId: string | null
  scores: ScoreAnnotationState[]
  onUpsertScore: (score: ScoreAnnotationState) => void
  availableSeries: CaseSeries[]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PiqualModulePanel({
  moduleId,
  seriesId,
  scores,
  onUpsertScore,
  availableSeries
}: PiqualModulePanelProps) {
  const [generatedId] = useState(() => crypto.randomUUID())

  // Detect DCE from series metadata
  const hasDCE = useMemo(
    () =>
      availableSeries.some((s) => {
        const desc = (s.series_description ?? "").toLowerCase()
        const imgType = (s.image_type ?? "").toLowerCase()
        return desc.includes("dce") || imgType.includes("dce")
      }),
    [availableSeries]
  )

  const existing = scores.find((s) => s.projectModuleId === moduleId && s.seriesId === seriesId && s.labelName === null)
  const currentId = existing?.id ?? generatedId
  const vals = existing?.values ?? {}

  // Reconstruct PIQUALScore from stored values
  const currentPiqual = useMemo((): PIQUALScore => {
    const result: PIQUALScore = { has_dce: hasDCE }
    for (const field of PIQUAL_QUESTION_FIELDS) {
      const v = vals[field]
      if (typeof v === "boolean") {
        result[field] = v
      }
    }
    // Compute derived scores
    return calculatePIQUALScore(result)
  }, [vals, hasDCE])

  const allAnswered = useMemo(() => {
    const baseAnswered =
      currentPiqual.axial_t2_snr !== undefined &&
      currentPiqual.axial_t2_delineate !== undefined &&
      currentPiqual.axial_t2_artifacts !== undefined &&
      currentPiqual.sag_coronal_t2_snr_and_artifacts !== undefined &&
      currentPiqual.dwi_contrast !== undefined &&
      currentPiqual.dwi_adc !== undefined &&
      currentPiqual.dwi_artifacts !== undefined &&
      currentPiqual.dwi_matching_adc_t2 !== undefined

    if (!hasDCE) return baseAnswered
    return baseAnswered && currentPiqual.dce_artifacts !== undefined && currentPiqual.dce_structures !== undefined
  }, [currentPiqual, hasDCE])

  const updateAnswer = useCallback(
    (field: BooleanFieldKey, value: boolean) => {
      // Build updated answers
      const updated: PIQUALScore = { has_dce: hasDCE }
      for (const f of PIQUAL_QUESTION_FIELDS) {
        const v = vals[f]
        if (typeof v === "boolean") {
          updated[f] = v
        }
      }
      updated[field] = value

      // Compute all derived scores
      const computed = calculatePIQUALScore(updated)

      // Persist all values (boolean answers + computed numeric scores)
      const newValues: Record<string, string | number | boolean | null> = { ...vals }
      for (const f of PIQUAL_QUESTION_FIELDS) {
        if (computed[f] !== undefined) {
          newValues[f] = computed[f]!
        }
      }
      newValues.has_dce = hasDCE
      newValues.t2_wi_score = computed.t2_wi_score ?? null
      newValues.dwi_score = computed.dwi_score ?? null
      newValues.dce_criteria_met = computed.dce_criteria_met ?? null
      newValues.score = computed.score ?? null

      onUpsertScore({
        id: currentId,
        seriesId,
        projectModuleId: moduleId,
        labelName: null,
        labelValue: null,
        values: newValues
      })
    },
    [vals, hasDCE, currentId, seriesId, moduleId, onUpsertScore]
  )

  // Filter sections — hide DCE if no DCE series
  const visibleSections = hasDCE ? SECTIONS : SECTIONS.filter((s) => s.title !== "DCE")

  return (
    <div className="flex-1 overflow-auto p-3 space-y-3">
      {/* Score banner */}
      {allAnswered && currentPiqual.score != null && (
        <div className="rounded-md px-3 py-2 text-center border border-border">
          <div className="text-sm font-semibold">PI-QUAL Score: {currentPiqual.score}/3</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {currentPiqual.score === 3 ? "Optimal" : currentPiqual.score === 2 ? "Acceptable" : "Inadequate"}
          </div>
        </div>
      )}

      {visibleSections.map((section) => {
        return (
          <div key={section.title} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <h3 className="text-[11px] font-semibold">{section.title}</h3>
            </div>
            <ol className="space-y-2">
              {section.questions.map((question, qIdx) => {
                const current = vals[question.id]
                return (
                  <li key={question.id} className="grid grid-cols-[1fr_auto] gap-2 items-start">
                    <span className="text-[11px] leading-tight">
                      {qIdx + 1}. {question.text}
                    </span>
                    <div className="flex gap-1 shrink-0 pt-px">
                      <button
                        onClick={() => updateAnswer(question.id, true)}
                        className={`px-2 py-0.5 rounded border text-[10px] font-medium transition-colors ${
                          current === true
                            ? "border-green-500/60 bg-green-500/10 text-green-600"
                            : "border-border bg-surface-raised hover:bg-accent/40"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => updateAnswer(question.id, false)}
                        className={`px-2 py-0.5 rounded border text-[10px] font-medium transition-colors ${
                          current === false
                            ? "border-red-500/60 bg-red-500/10 text-red-600"
                            : "border-border bg-surface-raised hover:bg-accent/40"
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </li>
                )
              })}
            </ol>
          </div>
        )
      })}
    </div>
  )
}
